import { VOMelody } from "../model/DataStructure.js";
import { Player, PlayerLoadOptions, PlayerState } from "./Player.js";

export interface MelodyPlayerOptions {
    onComplete?: (m: Player) => void;
    onTime?: (m: Player) => void;
    onStateChange?: (m: Player) => void;
    audioContext: AudioContext;
}

export class GrainPlayer implements Player {

    public onTime = e5.Signal.create<[Player]>();
    public onComplete = e5.Signal.create<[Player]>();
    public onStateChange = e5.Signal.create<[Player]>();

    private _options: MelodyPlayerOptions;
    private _targetTime: number = 0;
    private _contextTime: number = 0;
    private _part: Tone.GrainPlayer;
    private _state: PlayerState = "paused";
    private _duration: number = 0;
    private _pitch: number = 0;

    private _melody: VOMelody = null;
    private _context: AudioContext;
    private _speed: number = 1;
    private _buffer: Tone.Buffer;

    constructor(options: MelodyPlayerOptions) {

        options = e5.object.merge({
        }, options);
        this._options = options;

        this._context = options.audioContext;
        this._buffer = new Tone.Buffer(null);

        /**
         * @see https://github.com/Tonejs/Tone.js/blob/053b5d4397b595ea804b5d6baf6108158c8e0696/Tone/source/buffer/GrainPlayer.ts
         */
        this._part = new Tone.GrainPlayer(null);
        // this._part.grainSize = 0.05;
        this._part.toDestination();

        if (typeof options.onComplete === "function") {
            this.onComplete.add(options.onComplete);
        }

        if (typeof options.onTime === "function") {
            this.onTime.add(options.onTime);
        }

        if (typeof options.onStateChange === "function") {
            this.onStateChange.add(options.onStateChange);
        }

        e5.Player.onTick.add(() => {
            if (this._state !== "playing") {
                return;
            }

            if (this.time >= this.duration) {
                this.pause();

                this.onTime.dispatch(this);

                this.onComplete.dispatch(this);
            } else {
                this.onTime.dispatch(this);
            }
        });
    }

    public get state(): PlayerState {
        return this._state;
    }

    private setState(state: PlayerState): boolean {
        if (this._state === state) {
            return false;
        }
        this._state = state;
        this.onStateChange.dispatch(this);
        return true;
    }

    public toggle(): void {
        if (this._state === "playing") {
            this.pause();
        } else {
            this.play();
        }
    }

    public get duration(): number {
        if (!this._melody) {
            return 0;
        }
        return this._duration / this.speed;
    }

    public get time(): number {
        if (!this._melody) {
            return 0;
        }

        if (this._state === "paused") {
            return this._targetTime / this.speed;
        }

        let time = this._targetTime + (this._context.currentTime - this._contextTime) * this.speed;
        return e5.Calc.clamp(time / this.speed, 0, this.duration);
    }

    public set time(value: number) {
        value = e5.Calc.clamp(value, 0, this.duration);

        if (this._state === "paused") {
            this._targetTime = value * this.speed;
            this._contextTime = this._context.currentTime;

            this.onTime.dispatch(this);
            return;
        }

        this._targetTime = value * this.speed;
        this._contextTime = this._context.currentTime;
        
        this._part.stop(0);
        this._part.start(this._contextTime, this._targetTime / this._speed);
    }

    public play(): void {
        if (this._state === "playing"
            || !this._melody) {
            return;
        }

        this._part.stop(0);
        this._contextTime = this._context.currentTime;
        this._part.start(this._contextTime, this._targetTime / this._speed);

        this.setState("playing");
    }

    public pause(): void {
        if (this._state === "paused") {
            return;
        }

        this._targetTime = this.time * this.speed;
        this._contextTime = this._context.currentTime;

        this._part.stop(0);
        this.setState("paused");
    }

    public stop(): void {
        this.setState("paused");
        this._part.stop();
        this.time = 0;
    }

    public get pitch(): number {
        return this._pitch;
    }

    public set pitch(value: number) {
        if (this._pitch === value) {
            return;
        }
        this._pitch = value;
        this._part.detune = value * 100;
    }

    public get speed(): number {
        return this._speed;
    }

    public set speed(value: number) {
        if (this._speed === value) {
            return;
        }

        this._targetTime = this.time * this._speed;
        this._contextTime = this._context.currentTime;

        this._speed = value;

        this._part.playbackRate = value;

        this.onTime.dispatch(this);
    }

    public load(melody: VOMelody, options: PlayerLoadOptions = null): void {
        this._melody = melody;

        options = e5.object.merge<PlayerLoadOptions>(
            {
                pitch: 0,
                speed: 1
            }, options || ({} as any));


        this.pitch = options.pitch;
        this.speed = options.speed;

        this._targetTime = 0;
        this._contextTime = this._context.currentTime;


        const isPlaying = this._state === "playing";
        this._part.stop(0);

        if (melody) {

            this._duration = melody.duration;

            this._buffer.set(melody.sound.buffer);
            this._part.buffer = this._buffer;

            if (isPlaying) {
                this.time = 0;
                // this._part.start(0);
            }
        } else {
            this._duration = 0;

            this._part.stop();

            this.setState("paused");
        }
    }
}